import { useEffect, useState } from 'react'
import {
  ORG_PLANS,
  ORG_ROLE_HINT,
  ORG_ROLE_LABEL,
  type MeDto,
  type OrgDto,
  type OrgMemberDto,
  type OrgRole,
} from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Dialog } from './Dialog.js'
import { useText } from '../i18n.js'

/**
 * Die Menschen einer Organisation.
 *
 * Jedes Mitglied sieht, wer dazugehört; ändern kann nur die Verwaltung. Eine
 * Person, die es schon gibt, wird hier nicht neu angelegt, sondern bekommt eine
 * zweite Mitgliedschaft — das ist der Fall „ein Login, mehrere Unternehmen".
 */
export function TeamDialog({
  org,
  me,
  onClose,
  onChanged,
}: {
  org: OrgDto
  me: MeDto
  onClose: () => void
  onChanged: () => void
}): React.ReactElement {
  const t = useText()
  const [members, setMembers] = useState<OrgMemberDto[] | null>(null)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<OrgRole>('member')
  const [name, setName] = useState(org.name)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  /** Nur gesetzt, wenn der Mailversand scheiterte — dann von Hand weitergeben. */
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const isAdmin = org.role === 'admin'

  useEffect(() => {
    api
      .orgMembers(org.id)
      .then(setMembers)
      .catch(() => setMembers([]))
  }, [org.id])

  async function add(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setNote(null)
    setInviteUrl(null)
    setBusy(true)
    try {
      const result = await api.addOrgMember(org.id, { email, role })
      setMembers((current) => [...(current ?? []), result.member].sort(byName))
      setNote(
        result.invited
          ? t('team.invited', { email: result.member.email })
          : t('team.alreadyExisted', { name: result.member.displayName }),
      )
      setInviteUrl(result.inviteUrl)
      setEmail('')
      onChanged()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('team.addFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function changeRole(member: OrgMemberDto, next: OrgRole): Promise<void> {
    setError(null)
    try {
      await api.setOrgRole(org.id, member.userId, next)
      setMembers((current) =>
        (current ?? []).map((m) => (m.userId === member.userId ? { ...m, role: next } : m)),
      )
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('team.roleFailed'))
    }
  }

  async function remove(member: OrgMemberDto): Promise<void> {
    setError(null)
    try {
      const result = await api.removeOrgMember(org.id, member.userId)
      setMembers((current) => (current ?? []).filter((m) => m.userId !== member.userId))
      setNote(
        result.boardsHandedOver > 0
          ? t('team.removedWithBoards', {
              name: member.displayName,
              boards: t.plural(result.boardsHandedOver, 'team.board.one', 'team.board.many'),
            })
          : t('team.removed', { name: member.displayName }),
      )
      onChanged()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('team.removeFailed'))
    }
  }

  async function rename(): Promise<void> {
    const trimmed = name.trim()
    if (!trimmed || trimmed === org.name) return
    await api.renameOrg(org.id, trimmed)
    onChanged()
  }

  return (
    <Dialog title={`Organisation · ${org.name}`} onClose={onClose}>
      {isAdmin && (
        <label className="field">
          <span>{t('team.orgName')}</span>
          <input value={name} onChange={(event) => setName(event.target.value)} onBlur={rename} />
        </label>
      )}

      <h4 className="dialog-section">
        Mitglieder{' '}
        <span className="muted small">
          {members?.length ?? 0} von {org.license.memberLimit} im Paket „
          {t(ORG_PLANS[org.license.plan].label)}"
        </span>
      </h4>
      {members === null && <p className="muted small">{t('common.loading')}</p>}
      <ul className="member-list">
        {members?.map((member) => (
          <li key={member.userId}>
            <span className="member-name">
              {member.displayName}
              {member.userId === me.id && <span className="badge subtle">du</span>}
              {member.pending && (
                <span className="badge subtle" title={t('team.pendingTitle')}>
                  eingeladen
                </span>
              )}
            </span>
            <span className="member-mail muted small">{member.email}</span>
            {isAdmin && !member.pending ? (
              <select
                value={member.role}
                onChange={(event) => void changeRole(member, event.target.value as OrgRole)}
                title={t(ORG_ROLE_HINT[member.role])}
              >
                <option value="admin">{t(ORG_ROLE_LABEL.admin)}</option>
                <option value="member">{t(ORG_ROLE_LABEL.member)}</option>
              </select>
            ) : (
              <span className="badge subtle">{t(ORG_ROLE_LABEL[member.role])}</span>
            )}
            {isAdmin && (
              <button
                className="button ghost icon"
                onClick={() => void remove(member)}
                aria-label={`${member.displayName} entfernen`}
                title={
                  member.pending
                    ? t('team.withdrawInvite')
                    : t('team.removeFromOrg')
                }
              >
                ×
              </button>
            )}
          </li>
        ))}
      </ul>

      {isAdmin && (
        <form className="member-add" onSubmit={add}>
          <h4 className="dialog-section">{t('team.addPerson')}</h4>
          <label className="field">
            <span>E-Mail</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              placeholder={t('team.mailPlaceholder')}
            />
            <small>{t('team.addHint')}</small>
          </label>
          <label className="field">
            <span>{t('team.role')}</span>
            <select value={role} onChange={(event) => setRole(event.target.value as OrgRole)}>
              <option value="member">{t(ORG_ROLE_LABEL.member)}</option>
              <option value="admin">{t(ORG_ROLE_LABEL.admin)}</option>
            </select>
            <small>{t(ORG_ROLE_HINT[role])}</small>
          </label>
          <button className="button primary" type="submit" disabled={busy}>
            {busy ? t('common.moment') : t('team.add')}
          </button>
        </form>
      )}

      {note && <p className="muted small">{note}</p>}
      {/*
        Der Notausgang: Konnte die Mail nicht raus, hinge die eingeladene Person
        sonst ohne jeden Weg herein in der Luft. Der Link steht deshalb hier —
        einmal, zum Kopieren.
      */}
      {inviteUrl && (
        <p className="form-error">
          {t('team.inviteMailFailed')}
          <br />
          <code className="invite-link">{inviteUrl}</code>
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
    </Dialog>
  )
}

const byName = (a: OrgMemberDto, b: OrgMemberDto): number =>
  a.displayName.localeCompare(b.displayName, 'de')
